import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import { GoogleAdSlot, IGoogleAdConfig } from '../../../sharedComponents/types'
import { defaultGoogleAdsLazyLoadConfig, getPpid } from '../../../utils'
import { isMobile, isNativeApp } from '../../../client'

interface GoogleAdsContextType {
  isInitialized: boolean
  isLoading: boolean
  error: Error | null
  pathPlatform: null | 'Reshetmobile' | '13tv_app' | 'Reshet_desktop'
  registerSlotConfig: (slot: GoogleAdSlot) => void
  registerGPTSlot: (slotId: string, slot: googletag.Slot) => void
  refreshAd: (slotId: string) => void
  refreshAllAds: () => void
  destroyAd: (slotId: string) => void
  destroyAllAds: () => void
}

const GoogleAdsContext = createContext<GoogleAdsContextType | undefined>(
  undefined
)

interface GoogleAdsProviderProps {
  children: React.ReactNode
  config?: IGoogleAdConfig
}

export const GoogleAdsProvider: React.FC<GoogleAdsProviderProps> = ({
  children,
  config,
}) => {
  const router = useRouter()

  const [isInitialized, setIsInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [pathPlatform, setPathPlatform] = useState<
    null | 'Reshetmobile' | '13tv_app' | 'Reshet_desktop'
  >(null)

  /** Slot config (optional metadata) */
  const slotConfigsRef = useRef<Map<string, GoogleAdSlot>>(new Map())

  /** Actual GPT slots */
  const slotsRef = useRef<Map<string, googletag.Slot>>(new Map())

  useEffect(() => {
    if (isNativeApp()) {
      setPathPlatform('13tv_app')
    } else if (isMobile()) {
      setPathPlatform('Reshetmobile')
    } else {
      setPathPlatform('Reshet_desktop')
    }
  }, [])

  /* ---------------- GPT INIT ---------------- */

  useEffect(() => {
    const script = document.createElement('script')
    script.src =
      'https://securepubads.g.doubleclick.net/tag/js/gpt.js?network-code=36476105'
    script.async = true

    script.onload = () => {
      console.log('Google Publisher Tag loaded', config)
      window.googletag.cmd.push(() => {
        const pubads = window.googletag.pubads()
        pubads.setCentering(true)

        // GPT requires an alphanumeric-only ppid (32-150 chars) - a raw UUID's
        // hyphens fail that and GAM silently drops it, unlike the video IMA
        // ppid which accepts the value as-is.
        pubads.setPublisherProvidedId(getPpid())

        let isEnableLazyLoad = false

        // if (config?.enableSingleRequest) {
        //   isEnableSingleRequest = pubads.enableSingleRequest()
        // }
        pubads.collapseEmptyDivs(!!config?.collapseEmptyDivs)
        if (config?.enableLazyLoad && config.lazyLoadConfig) {
          isEnableLazyLoad = true
          pubads.enableLazyLoad(
            config.lazyLoadConfig || defaultGoogleAdsLazyLoadConfig
          )
        }
        console.log('Google Publisher Tag', {
          isEnableLazyLoad,
          collapseEmptyDivs: !!config?.collapseEmptyDivs,
        })

        if (config?.enableServices !== false) {
          window.googletag.enableServices()
        }

        setIsInitialized(true)
        setIsLoading(false)
      })
    }

    script.onerror = () => {
      setError(new Error('Failed to load Google Publisher Tag'))
      setIsLoading(false)
    }

    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [config])

  /* ---------------- SLOT REGISTRATION ---------------- */

  const registerSlotConfig = useCallback((slot: GoogleAdSlot) => {
    slotConfigsRef.current.set(slot.id, slot)
  }, [])

  const registerGPTSlot = useCallback(
    (slotId: string, slot: googletag.Slot) => {
      slotsRef.current.set(slotId, slot)
    },
    []
  )

  /* ---------------- DESTROY ---------------- */

  const destroyAd = useCallback((slotId: string) => {
    const slot = slotsRef.current.get(slotId)
    if (!slot || !window.googletag) {
      return
    }

    window.googletag.cmd.push(() => {
      window.googletag.destroySlots([slot])
    })

    slotsRef.current.delete(slotId)
    slotConfigsRef.current.delete(slotId)

    const el = document.getElementById(slotId)
    if (el) {
      el.innerHTML = ''
    }
  }, [])

  const destroyAllAds = useCallback(() => {
    const allSlots = Array.from(slotsRef.current.values())
    if (!allSlots.length || !window.googletag) {
      return
    }

    window.googletag.cmd.push(() => {
      window.googletag.destroySlots(allSlots)
    })

    slotsRef.current.clear()
    slotConfigsRef.current.clear()

    document
      .querySelectorAll('[id^="gpt-"], [data-gpt-slot]')
      .forEach(el => (el.innerHTML = ''))
  }, [])

  /* ---------------- REFRESH ---------------- */

  const refreshAd = useCallback((slotId: string) => {
    const slot = slotsRef.current.get(slotId)
    if (!slot || !window.googletag) {
      return
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh([slot])
    })
  }, [])

  const refreshAllAds = useCallback(() => {
    const slots = Array.from(slotsRef.current.values())
    if (!slots.length || !window.googletag) {
      return
    }

    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh(slots)
    })
  }, [])

  /* ---------------- ROUTE CHANGE CLEANUP ---------------- */

  useEffect(() => {
    const handleRouteChange = () => {
      destroyAllAds()
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events, destroyAllAds])

  /* ---------------- CONTEXT ---------------- */

  const contextValue: GoogleAdsContextType = {
    isInitialized,
    isLoading,
    error,
    pathPlatform,
    registerSlotConfig,
    registerGPTSlot,
    refreshAd,
    refreshAllAds,
    destroyAd,
    destroyAllAds,
  }

  return (
    <GoogleAdsContext.Provider value={contextValue}>
      {children}
    </GoogleAdsContext.Provider>
  )
}

/* ---------------- HOOK ---------------- */

export const useGoogleAds = (): GoogleAdsContextType => {
  const context = useContext(GoogleAdsContext)
  if (!context) {
    throw new Error('useGoogleAds must be used within GoogleAdsProvider')
  }
  return context
}
